import { useState } from "react";
import { IconButton, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import EditActivity from "../EditActivity/EditActivity";
import PopUpMenu from "../../PopUpMenu/PopUpMenu";
import MenuItem from "@mui/material/MenuItem";
import ButtonLoading from "../../Button/ButtonLoading";
import LegalbirdIoModal from "../../Modal/LegalbirdIoModal";
import CalendlyAssessmentModal from "../../CalendlyAssessments/CalendlyAssessmentModal";
import EditActivityNote from "./EditActivityNote";
import { Delete, LocalPhoneOutlined } from "@mui/icons-material";
import {
  canOpenCalendlyAssessmentModal,
  getAvailableActivityActions,
  isCalendlyActivity,
} from "../../../services/Activities/activityFunctions";
import ActivityDoneCheckBox from "./ActivityDoneCheckbox";
import { Activity } from "../../../types/Activity";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys, updateResource } from "../../../services/ReactQuery/reactQueryService";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { activityActionsStyles } from "./activityActionsStyles";
import { useOptionalCall } from "../../../provider/CallProvider";
import { useOptionalDrawer } from "../../../provider/DrawerProvider";
import { ProductUnion } from "../../../types/ProductUnion";
import { useOptionalAiChat } from "../../../provider/AiChatProvider";
import FloatingAgentButton from "../../AiChat/FloatingAgentButton";
import { useBackofficeUser } from "../../../provider/BackofficeUserProvider";
import ReactGA from "react-ga4";

type ActivityActionsProps = {
  activity: Activity;
  updateActivities: () => Promise<void> | void;
  product?: ProductUnion;
};

const ActivityActions = ({ activity, updateActivities, product }: ActivityActionsProps) => {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const currentUser = useCurrentUser();
  const updateMutation = useMutation({
    mutationFn: updateResource
  });
  const openCallModal = useOptionalCall()?.openCallModal;
  const queryClient = useQueryClient();
  const drawer = useOptionalDrawer();
  const aiChat = useOptionalAiChat();
  const { backofficeUsers } = useBackofficeUser();

  const handleEdit = () => {
    if (drawer) {
      drawer.setDrawerState({ type: "activity", entityData: activity });
    } else {
      setEditOpen(true);
    }
  };

  const availableActions = getAvailableActivityActions(activity, currentUser, backofficeUsers, product);

  const handleDeleteActivity = async () => {
    setIsLoading(true);
    await updateMutation.mutateAsync({
      uri: "activities",
      id: activity.id || 0,
      data: {
        deleted: true,
      },
    });
    setIsLoading(false);
    updateActivities();
    setDeleteOpen(false);
  };

  const toggleActivityDone = async () => {
    if (isCalendlyActivity(activity)) {
      if (!activity.done) {
        setAssessmentOpen(true);
      }
      return;
    }
    setIsLoading(true);
    await updateMutation.mutateAsync({
      uri: "activities",
      id: activity.id || 0,
      data: {
        done: !activity.done,
      },
    });
    updateActivities();
  };

  return (
    <>
      <Grid
        container
        wrap={"nowrap"}
        sx={{
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {aiChat && availableActions.canDelegateToAgent && (
          <Grid sx={activityActionsStyles.actionItem}>
            <FloatingAgentButton
              size={"small"}
              disabled={aiChat.isOpen}
              onClick={() => {
                ReactGA.event({
                  category: "UserAction",
                  action: "delegateActivityToAgent",
                });
                aiChat.openAiChat(`Bearbeite die Aktivität "${activity.subject || "Aktivität"}" mit der ID ${activity.id}.`);
              }}
            />
          </Grid>
        )}

        <Grid sx={activityActionsStyles.actionItem}>
          <ActivityDoneCheckBox
            activityDone={activity.done}
            isLoading={isLoading}
            toggleActivityDone={toggleActivityDone}
            isCheckboxDisabled={!availableActions.canComplete}
          />
        </Grid>

        {availableActions.canEditActivityNote && (
          <Grid sx={activityActionsStyles.actionItem}>
            <EditActivityNote activity={activity} updateActivities={updateActivities} />
          </Grid>
        )}

        {availableActions.canEdit && (
          <Grid sx={activityActionsStyles.actionItem}>
            <PopUpMenu tooltipTitle={"Mehr"}>
              {availableActions.canEdit && <MenuItem onClick={handleEdit}>Bearbeiten</MenuItem>}
              {availableActions.canDelete && <MenuItem onClick={() => setDeleteOpen(true)}>Löschen</MenuItem>}
            </PopUpMenu>
          </Grid>
        )}

        {availableActions.canCall && product && openCallModal && (
          <Grid sx={activityActionsStyles.actionItem}>
            <IconButton
              onClick={() =>
                openCallModal(activity, () => {
                  queryClient.invalidateQueries({
                    queryKey: queryKeys.collections("activities")
                  });
                  queryClient.invalidateQueries({
                    queryKey: queryKeys.collections("phone_calls")
                  });
                })
              }
            >
              <LocalPhoneOutlined />
            </IconButton>
          </Grid>
        )}

        {availableActions.canDelete && !availableActions.canEdit && (
          <Grid sx={activityActionsStyles.actionItem}>
            <IconButton onClick={() => setDeleteOpen(true)}>
              <Delete />
            </IconButton>
          </Grid>
        )}
      </Grid>
      {canOpenCalendlyAssessmentModal(activity, product) && (
        <CalendlyAssessmentModal
          key={"calendlyAssessmentModal." + activity.id}
          open={assessmentOpen}
          handleClose={() => setAssessmentOpen(false)}
          activity={activity}
          product={product}
          update={updateActivities}
        />
      )}
      {availableActions.canEdit && (
        <EditActivity
          key={activity.id}
          activity={activity}
          open={editOpen}
          closeDialog={() => setEditOpen(false)}
          updateActivities={updateActivities}
        />
      )}
      {availableActions.canDelete && (
        <LegalbirdIoModal
          handleClose={() => setDeleteOpen(false)}
          open={deleteOpen}
          title={"Achtung"}
          submitButton={
            <ButtonLoading variant={"contained"} onClick={handleDeleteActivity} isLoading={isLoading}>
              Ja
            </ButtonLoading>
          }
        >
          <Typography sx={activityActionsStyles.deleteDialogText}>
            Diese Aktivität wird unwiderruflich gelöscht. Sind Sie sicher, dass Sie die Aktivität löschen möchten?
          </Typography>
        </LegalbirdIoModal>
      )}
    </>
  );
};

export default ActivityActions;
